import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  AccountMenuComponent as SharedAccountMenuComponent,
  type AccountMenuItem,
  profileMenuItems,
} from '@lite-workspace/shared/blocks/account-menu';
import { IdentityStore } from '@lite-workspace/shared/identity';
import { SupportWidgetService } from '@lite-workspace/shared/support-widget';
import { bffLogout, clearAuth, isBffMode, redirectToBffLogin, redirectToIdentity } from '../core/auth';
import { AccountService } from '../core/account.service';

/**
 * Gallery's side of the shared profile menu: the markup comes from the shared lib and only the
 * cabinet-specific parts live here — the account rows, the support link and the sign-out. The
 * avatar photo rides in from the portfolio visitka, which identity itself does not carry.
 * The wrapper keeps its own tag: `app-account-menu` inside this template is the SHARED component,
 * and reusing that selector here would make both match the same node (NG8023).
 */
@Component({
  selector: 'gallery-account-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedAccountMenuComponent],
  template: `<app-account-menu [items]="items" [avatarUrl]="avatarUrl()" (logoutConfirmed)="logout()" />`,
})
export class AccountMenuComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly account = inject(AccountService);
  private readonly identity = inject(IdentityStore);
  private readonly supportWidget = inject(SupportWidgetService);

  protected readonly avatarUrl = computed(() => this.account.summary()?.avatarUrl ?? null);

  protected readonly items: readonly AccountMenuItem[] = profileMenuItems(() => this.go('/account'));

  ngOnInit(): void {
    // The avatar rides in from this cabinet's own summary; the name/email line comes from the
    // account profile through the gallery identity adapter.
    void this.account.load();
    this.identity.loadProfile();
  }

  protected go(path: string): void {
    void this.router.navigate([path]);
  }

  protected async logout(): Promise<void> {
    this.supportWidget.stop();
    clearAuth();
    if (isBffMode()) {
      // The server-side session has to die too, otherwise the next guard probe still reports it alive.
      await bffLogout();
      redirectToBffLogin();
      return;
    }

    redirectToIdentity();
  }
}
